import { RawIntlProvider, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Header } from '@liveauctioneers/hammer-ui-liveauctioneers/header';
import { en } from '@liveauctioneers/hammer-ui-liveauctioneers/intl/en';
import { HeaderProps, RecentSearch, SavedSeller } from '@liveauctioneers/hammer-ui-liveauctioneers/types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { logOutBidder } from '@/redux/modules/account/logout/logout.actions';
import { getBidderId, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { getMyBidItemIds } from '@/redux/modules/bidder/biddingInfoAll/bidderBiddingInfoAll.selectors';
import { getUnreadInboxCount } from '@/redux/modules/conversation/folders/conversationFolders.selectors';
import { getFollowedSellerIds } from '@/redux/modules/followSeller';
import { getSavedItemsCount } from '@/redux/modules/item/saved/savedItems.selectors';
import { CompleteAccountModalContext, openCompleteAccountSignUpModal, openLoginModal } from '@/redux/modules/modal';
import { deleteRecentSearch, fetchRecentSearches, getHeaderRecentSearches } from '@/redux/modules/saveSearch';
import { fetchSellersIfNeeded, getHousesById } from '@/redux/modules/seller';
import { fetchIncompleteOrdersCountIfNeeded } from '@/redux/modules/wonItems/wonItems.actions';
import { getIncompleteOrderCount } from '@/redux/modules/wonItems/wonItems.selectors';
import { SEARCH_QUERY_MAX_LENGTH } from '@/types/search/searchQuery';
import { HeaderNavigationEventType, useTrackHeaderCategoryClickedAnalytics } from '@/utils/amplitude/analytics';
import {
    AnalyticsCTAEvent,
    useTrackCTAClickedAnalytics,
} from '@/utils/amplitude/analytics/cta/trackCTAClicked.analytics';
import {
    HowToWinLinkLocation,
    useTrackHowToWinClickedAnalytics,
} from '@/utils/amplitude/analytics/howToWin/trackHowToWinClicked.analytics';
import {
    useTrackHeaderNavigationClicked,
    useTrackNavMenuClickedAnalytics,
} from '@/utils/amplitude/analytics/navigation';
import { useMyItemsHoverMenuExperiment } from '@/utils/amplitude/experiments/hooks/useMyItemsHoverMenu.experiment';
import { useSearchInputRedesignExperiment } from '@/utils/amplitude/experiments/hooks/useSearchInputRedesignExperiment.experiment';
import { getItemIdFromPath } from '@/utils/urls';

import { EnableSmsOnSavePrompt } from './components/EnableSmsOnSavePrompt/EnableSmsOnSavePrompt';
import { SearchByImageTrailingButton } from './components/SearchByImageEntryPoint/SearchByImageTrailingButton';
import { useBrowserAlertsProps } from './hooks/useBrowserAlertsProps';
import { useCategoryNavigationAdsProps } from './hooks/useCategoryNavigationAdsProps';
import { useNotificationDataProps } from './hooks/useNotificationDataProps';
import { useSearchProps } from './hooks/useSearchProps';
import { useWhiteLabelProps } from './hooks/useWhiteLabelProps';

const MAXIMUM_WON_ITEMS_DISPLAY_COUNT = 10;
const MAX_SAVED_SELLERS_IN_SEARCH = 5;
const NAVIGATION_DROPDOWN_Z_INDEX_LAYER = 12;

type HeaderNavMenuProps = {
    myItemsHoverMenuEnabled?: boolean;
    onNavMenuClicked?: (payload: { menuName: string; parentMenu: string }) => void;
    savedItemsCount?: number;
};

export const HammerHeader = () => {
    const dispatch = useAppDispatch();
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const unreadCount = useAppSelector(getUnreadInboxCount);
    const savedItemsCount = useAppSelector(getSavedItemsCount);
    const incompleteOrdersCount = useAppSelector(getIncompleteOrderCount);
    const { upcomingBidItemIds = [] } = useAppSelector(getMyBidItemIds);
    const upcomingBidsCount = upcomingBidItemIds.length;
    const bidderId = useAppSelector(getBidderId);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const itemId = getItemIdFromPath(pathname);
    const followedSellerIds = useAppSelector(getFollowedSellerIds);
    const followedSellers = useAppSelector((state) => getHousesById(state, followedSellerIds));
    const trackHeaderCategoryClicked = useTrackHeaderCategoryClickedAnalytics();

    const onLogoutClicked = useCallback(() => dispatch(logOutBidder()), [dispatch]);

    const trackCTAClicked = useTrackCTAClickedAnalytics();
    const trackHeaderNavigationClicked = useTrackHeaderNavigationClicked();
    const trackHowToWinClicked = useTrackHowToWinClickedAnalytics();
    const trackNavMenuClicked = useTrackNavMenuClickedAnalytics();
    const isMyItemsHoverMenuTreatment = useMyItemsHoverMenuExperiment();
    const { isShowingFeature: hasSearchInputRedesignExperiment, trackFeatureFlag: trackSearchInputRedesignExposure } =
        useSearchInputRedesignExperiment({ expose: false });
    const onLoginClicked = useCallback(() => {
        trackCTAClicked(AnalyticsCTAEvent.HammerHeader.Login);
        dispatch(
            openLoginModal({
                eventContext: EventContext.join,
                id: itemId,
                itemId,
                type: CompleteAccountModalContext.Login,
            })
        );
    }, [dispatch, itemId, trackCTAClicked]);

    const onSignUpClicked = useCallback(() => {
        trackCTAClicked(AnalyticsCTAEvent.HammerHeader.Join);
        dispatch(
            openCompleteAccountSignUpModal({
                eventContext: EventContext.join,
                id: itemId,
                itemId,
                type: CompleteAccountModalContext.Join,
            })
        );
    }, [dispatch, itemId, trackCTAClicked]);

    const onTrackCategoryClick = useCallback(
        (categoryName: string) => {
            trackHeaderCategoryClicked({ categoryName });
        },
        [trackHeaderCategoryClicked]
    );

    useEffect(() => {
        if (bidderId > 0) {
            dispatch(
                fetchIncompleteOrdersCountIfNeeded({
                    bidderId,
                    force: Boolean(bidderId),
                })
            );
        }
    }, [bidderId, dispatch]);

    useEffect(() => {
        if (followedSellerIds.length > 0) {
            dispatch(fetchSellersIfNeeded(followedSellerIds));
        }
    }, [dispatch, followedSellerIds]);

    // fetchRecentSearches reads savedsearch-api when logged in and the laRecentSearches cookie when
    // logged out, so the list belongs to whichever identity was active when it loaded. Forcing on a
    // change of userLoggedIn is what discards the other identity's list; the thunk's own five-minute
    // cache would otherwise keep it. Keyed on the auth state rather than on a login action because
    // the token is set by five flows (login, login-with-token, confirm-password, create-account,
    // complete-account) and only two of them dispatch LOGIN_SUCCESS. Not gated on bidderId, unlike
    // the effects above: the thunk branches on userLoggedIn, and a logged-out visitor has -1.
    const loadedWhileLoggedIn = useRef<boolean | null>(null);

    useEffect(() => {
        if (!hasSearchInputRedesignExperiment) {
            return;
        }

        const authChanged = loadedWhileLoggedIn.current !== null && loadedWhileLoggedIn.current !== userLoggedIn;
        loadedWhileLoggedIn.current = userLoggedIn;

        dispatch(fetchRecentSearches({ force: authChanged }));
    }, [dispatch, hasSearchInputRedesignExperiment, userLoggedIn]);

    const searchProps = useSearchProps();
    // The app's intl (captured here, in the app tree). The hammer-ui Header mounts its
    // own IntlProvider with only hammer-ui messages, which shadows the app's for anything
    // rendered inside the header — the search-by-image triggers need the app intl re-provided.
    // useAppFormatMessage can't substitute: RawIntlProvider requires the full intl instance,
    // not just formatMessage.
    const appIntl = useIntl();
    const headerRecentSearches = useAppSelector(getHeaderRecentSearches);
    const recentSearchUrlByQuery = useMemo(
        () => new Map(headerRecentSearches.map(({ query, url }) => [query, url])),
        [headerRecentSearches]
    );

    const recentSearchHashByQuery = useMemo(
        () => new Map(headerRecentSearches.map(({ hash, query }) => [query, hash])),
        [headerRecentSearches]
    );

    const recentSearches = useMemo(() => headerRecentSearches.map(({ query }) => ({ query })), [headerRecentSearches]);

    const handleRecentSearchClick = useCallback(
        (recentSearch: RecentSearch) => {
            const recentSearchUrl = recentSearchUrlByQuery.get(recentSearch.query);

            if (recentSearchUrl) {
                navigate(recentSearchUrl, { state: { suggestionClicked: true } });
            }
        },
        [navigate, recentSearchUrlByQuery]
    );

    const handleRecentSearchDelete = useCallback(
        (recentSearch: RecentSearch) => {
            const hash = recentSearchHashByQuery.get(recentSearch.query);

            if (hash) {
                dispatch(deleteRecentSearch({ hash }));
            }
        },
        [dispatch, recentSearchHashByQuery]
    );

    const savedSellers = useMemo(
        (): SavedSeller[] =>
            followedSellers
                .slice(0, MAX_SAVED_SELLERS_IN_SEARCH)
                .flatMap((seller) => (seller?.name ? [{ sellerId: seller.sellerId, sellerName: seller.name }] : [])),
        [followedSellers]
    );

    const handleSavedSellerClick = useCallback(
        (savedSeller: SavedSeller) => {
            searchProps.handleSearch({
                houseId: savedSeller.sellerId,
                query: savedSeller.sellerName,
                suggestionClicked: true,
            });
        },
        [searchProps]
    );

    const handleSearchInputFocus = useCallback(() => {
        trackSearchInputRedesignExposure();
    }, [trackSearchInputRedesignExposure]);

    const headerProps: HeaderProps & HeaderNavMenuProps = {
        browserAlerts: useBrowserAlertsProps(),
        callbacks: {
            onAlertsClicked: () => {
                trackHeaderNavigationClicked({
                    eventType: HeaderNavigationEventType.Alerts,
                });
            },
            onHowToWinClicked: () => {
                trackHowToWinClicked(HowToWinLinkLocation.NavHeader);
            },
        },
        categoryNavigationAds: useCategoryNavigationAdsProps(),
        intl: en,
        isUserLoggedIn: userLoggedIn,
        myItemsHoverMenuEnabled: isMyItemsHoverMenuTreatment,
        notificationData: useNotificationDataProps(),
        onLoginClicked,
        onLogoutClicked,
        onNavMenuClicked: ({ menuName, parentMenu }) => {
            trackNavMenuClicked({ menuName, parentMenu });
        },
        onSignUpClicked,
        search: {
            ...searchProps,
            maxLength: SEARCH_QUERY_MAX_LENGTH,
            trailingContent: (
                // hammer-ui's Header mounts its own IntlProvider, so the trigger needs the app's
                // intl bridged back in or its copy renders as raw visualSearch.* keys.
                <RawIntlProvider value={appIntl}>
                    <SearchByImageTrailingButton />
                </RawIntlProvider>
            ),
        },
        searchInputRedesignExperiment: {
            enabled: hasSearchInputRedesignExperiment,
            handleRecentSearchClick,
            handleRecentSearchDelete,
            handleSavedSellerClick,
            handleSearchInputFocus,
            recentSearches,
            savedSellers,
            showQueryEcho: searchProps.hasNoSuggestionsForQuery,
        },
        trackCategoryClick: onTrackCategoryClick,
        savedItemsCount,
        unreadCount,
        upcomingBidsCount,
        wonItemsCount: incompleteOrdersCount,
        wonItemsMaximumCount: MAXIMUM_WON_ITEMS_DISPLAY_COUNT,
        ...useWhiteLabelProps(),
        zIndexes: {
            megaNavDropdown: NAVIGATION_DROPDOWN_Z_INDEX_LAYER,
            searchDropdown: NAVIGATION_DROPDOWN_Z_INDEX_LAYER,
        },
    };

    // Key by search queryText so the header search input remounts when URL keyword changes (e.g. pill click) and shows the new value
    const headerKey = `search-${headerProps.search.queryText ?? ''}`;

    return (
        <>
            <Header
                key={headerKey}
                {...headerProps}
            />
            <EnableSmsOnSavePrompt />
        </>
    );
};
